<template>
  <table class="policy-table">
    <thead>
      <tr>
        <th class="policy-table__header"></th>
        <th
          v-for="(xCategory, xIndex) in xCategories"
          :key="`category${xIndex}`"
          class="policy-table__header"
        >
          {{ xCategory.name }}
          <ReferenceModal
            v-if="xCategory.note"
            :refIndex="hash(xCategory.name)"
            :modalTitle="xCategory.name"
          >
            {{ xCategory.note }}
            <template #reference> ? </template>
          </ReferenceModal>
        </th>
      </tr>
    </thead>
    <tbody>
      <DataGetter
        v-for="(yCategory, yIndex) in yCategories"
        :key="`row${yIndex}`"
        :url="apiRoute"
        :params="{
          ...params,
          ...yCategory.params,
        }"
        v-slot="{ data, loading }"
        child-element-type="tr"
      >
        <td>
          <p class="policy-table__row-name">{{ yCategory.name }}</p>
        </td>
        <td v-for="(xCategory, xIndex) in xCategories" :key="`row${xIndex}`">
          <span
            v-if="loading"
            class="policy-table-item__loading"
            title="Loading..."
          />
          <template v-else>
            <a
              v-if="getMatchingPolicies(data, xCategory).length > 0"
              :href="getQueryString(xCategory, yCategory)"
              target="_blank"
            >
              <span
                :class="
                  `policy-table-item__status policy-table-item__status--${
                    isPartial(data, xCategory) ? 'partial' : 'full'
                  }`
                "
                title="View matching policies in IEA policy database"
              >
                <span v-if="showNumber">{{
                  getMatchingPolicies(data, xCategory).length
                }}</span>
              </span>
            </a>
          </template>
        </td>
      </DataGetter>
    </tbody>
  </table>
</template>

<script>
import qs from "qs";
import merge from "deepmerge";
import DataGetter from "./DataGetter.vue";
import ReferenceModal from "./ReferenceModal.vue";

export default {
  components: { DataGetter, ReferenceModal },
  props: {
    // [ { name, params, isOr, note } ]
    xCategories: {
      type: Array,
      required: true,
    },
    // [ { name, params, isOr } ]
    yCategories: {
      type: Array,
      required: true,
    },
    // If provided, some policy must meet these params for a "full" status, otherwise
    // it gets a partial one
    fullParams: {
      type: Object,
      default: null,
    },
    // Params that all policies must meet
    params: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },
    // show the number of policies (on by default)
    showNumber: {
      type: Boolean,
      default: true,
    },
    // allow alternative array format. For PAMS should leave as indices.
    arrayFormat: {
      type: String,
      default: "indices",
    },
    // show policy IDs and use test api
    debug: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      apiRoute: `https://api${this.debug ? ".test." : "."}iea.org/policies/v2/`,
    };
  },
  methods: {
    policyMatchesParams(policy, params, isOr) {
      // treat as AND within and between by default
      const paramKeys = Object.keys(params);
      for (let i = 0; i < paramKeys.length; i += 1) {
        let match = false;
        const param = paramKeys[i];
        if (param === "isOr") continue;
        const paramValue = params[param];
        const policyParamValue = policy[param];
        // NB param values should always be an array
        if (Array.isArray(policyParamValue)) {
          match = isOr
            ? paramValue.some((value) => policyParamValue.includes(value))
            : paramValue.every((value) => policyParamValue.includes(value));
        } else {
          match = isOr
            ? paramValue.some((value) => value === policyParamValue)
            : paramValue.every((value) => value === policyParamValue);
        }
        if (!match) return false;
      }
      return true;
    },
    getMatchingPolicies(yPolicies, xCategory) {
      const { params, isOr } = xCategory;
      return yPolicies.filter((policy) =>
        this.policyMatchesParams(policy, params, isOr)
      );
    },
    getQueryString(yCategory, xCategory) {
      const { params, arrayFormat, debug } = this;
      const qsParams = merge.all([params, yCategory.params, xCategory.params]);
      return `https://${
        debug ? "test" : "www"
      }.iea.org/policies/?${qs.stringify(qsParams, {
        arrayFormat,
        encodeValuesOnly: true,
      })}`;
    },
    isPartial(policies, xCategory) {
      const { fullParams } = this;
      return !fullParams
        ? false
        : !policies.some((policy) =>
            this.policyMatchesParams(policy, {
              ...fullParams,
              ...xCategory.params,
            })
          );
    },
    // Generates a unique numerical hash to avoid refIndex clashes among multiple components
    hash(s) {
      let h;
      for (let i = 0; i < s.length; i++)
        h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;

      return h;
    },
  },
};
</script>

<style scoped>
.policy-table-item__status {
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: block;
  box-sizing: border-box;
  margin: 1em auto;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.policy-table-item__status a {
  color: inherit;
  text-decoration: none;
  box-shadow: none !important;
}
.policy-table-item__status--full {
  background: #04f;
  color: #fff;
}
.policy-table-item__status--partial {
  border-width: 4px;
  border-style: solid;
  border-color: #04f;
  color: #6f6f6f;
}
.policy-table-item__loading {
  border-radius: 50%;
  width: 12px;
  height: 12px;
  display: block;
  box-sizing: border-box;
  margin: 1em auto;
  border-width: 2px;
  border-style: solid;
  border-color: #efefef;
}
table.policy-table {
  table-layout: fixed;
  border-collapse: collapse;
  width: 100%;
  margin: 16px auto;
}
table.policy-table,
.policy-table td {
  background: transparent;
  border: none;
}
table.policy-table th.policy-table__header {
  font-weight: 600;
  background-color: transparent;
}
.policy-table tr {
  height: 3.5rem;
}
.policy-table tr td {
  margin: 0;
  padding: 0.1em 1em 0.1em 0;
  border-top: 1px solid #e6e6e6;
  vertical-align: middle;
}
.policy-table__header,
.policy-table tr td,
.policy-table tr th {
  position: relative;
}
.policy-table__legend--full {
  background: #04f;
}
.policy-table__legend--partial {
  border-width: 4px;
  border-style: solid;
  border-color: #04f;
}
</style>
